import communicationProductService from '../../../services/provider/communicationProduct'
import notisphereCommunicationProductService from '../../../services/notisphere/communicationProduct'
import delegateService from '../../../services/provider/delegates'
import { normalizeProductFieldDataType } from '../../../misc/commons'

const DELEGATE_WEBFORM_ROUTES = ['delegate-webform', 'supplier-delegate-webform']

const TIER_LABELS = {
    1: 'Product Family',
    2: 'Product Variant',
    3: 'Batch & Lot',
    4: 'Shipping & Logistics'
}

const TIER_ICONS = {
    0: 'fa-box',
    1: 'fa-layer-group',
    2: 'fa-code-branch',
    3: 'fa-boxes',
    4: 'fa-truck'
}

export default {
    name: 'ProviderProductTierView',
    components: {
        ImportProductModal: require('../communication/step1-products/import-product-modal/index.vue').default
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        recallName: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showAddProduct: {
            type: Boolean,
            default: true
        },
        showImport: {
            type: Boolean,
            default: false
        },
        communicationId: {
            type: String,
            default: null
        },
        productFieldMetaList: {
            type: Array,
            default: () => []
        },
        fieldDefinitions: {
            type: Array,
            default: null
        },
        // Only the recall-response form tracks per-product Quantity / Unit of Measure
        showQuantityUnit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            products: [],
            editingTier: null,
            tempTierValues: {},
            expandedProducts: {},
            isAddingNewProduct: false,
            activeToasts: new Set(),
            syncingFromProp: false,
            importProductModalKey: 0,
            definitions: [],
            definitionsLoaded: false
        }
    },
    computed: {
        isEditing() {
            return !!(this.editingTier || this.isAddingNewProduct)
        },
        fieldsByTier() {
            const map = { 1: [], 2: [], 3: [], 4: [] }
            ;(this.definitions || []).forEach(def => {
                const tier = Number(def.tier_level)
                if (map[tier]) map[tier].push(def)
            })
            return map
        }
    },
    watch: {
        value: {
            handler(newVal) {
                this.syncingFromProp = true
                this.products = Array.isArray(newVal)
                    ? JSON.parse(JSON.stringify(newVal))
                    : []
                this.$nextTick(() => {
                    this.syncingFromProp = false
                })
            },
            immediate: true,
            deep: true
        },
        fieldDefinitions: {
            handler(val) {
                if (Array.isArray(val) && val.length) {
                    this.definitions = val
                    this.definitionsLoaded = true
                }
            },
            immediate: true,
            deep: true
        },
        isEditing(newValue) {
            this.$emit('editing-state-changed', newValue)
        }
    },
    created() {
        this.ensureDefinitionsLoaded()
    },
    methods: {
        tierLabel(level) {
            return TIER_LABELS[level] || `Tier ${level}`
        },
        tierIcon(level) {
            return TIER_ICONS[level] || 'fa-circle'
        },
        isDateField(def) {
            const t = (def?.data_type || '').toLowerCase()
            return t === 'date' || t === 'datetime'
        },
        ensureDefinitionsLoaded() {
            if (Array.isArray(this.fieldDefinitions) && this.fieldDefinitions.length) {
                this.definitions = this.fieldDefinitions
                this.definitionsLoaded = true
                return Promise.resolve(this.definitions)
            }
            if (this.definitionsLoaded && this.definitions.length) {
                return Promise.resolve(this.definitions)
            }
            const service = this.$route?.name === 'noti_RecallReview' || this.$route?.name === 'noti_RecallsFeed' || this.$route?.name === 'noti_RecallView'
                ? notisphereCommunicationProductService
                : DELEGATE_WEBFORM_ROUTES.includes(this.$route?.name)
                    ? delegateService
                    : communicationProductService
            return service.getFieldDefinitions().then(resp => {
                if (resp && !resp.error) {
                    const data = resp.data?.d ?? resp.data ?? []
                    this.definitions = Array.isArray(data) ? data : []
                    this.definitionsLoaded = true
                }
                return this.definitions
            }).catch(() => {
                this.definitions = []
                return this.definitions
            })
        },

        emitProducts(change) {
            const next = JSON.parse(JSON.stringify(this.products))
            this.$emit('input', next)
            if (change) {
                this.$emit('change', change)
            }
        },

        showImportProductModal() {
            if (!this.communicationId) {
                this.showToast('Please save recall info before importing products', {
                    title: 'Validation Error',
                    variant: 'danger'
                })
                return
            }
            this.ensureDefinitionsLoaded().then(() => {
                this.importProductModalKey += 1
                this.$nextTick(() => {
                    if (!this.$refs.importProductModal) return
                    const meta = (this.definitions || []).map(d => ({
                        name: d.field_name,
                        type: normalizeProductFieldDataType(d.data_type),
                        field_key: d.field_key,
                        tier_level: d.tier_level
                    }))
                    const fallbackMeta = (this.productFieldMetaList || []).map(x => ({
                        ...x,
                        type: normalizeProductFieldDataType(x.type)
                    }))
                    this.$refs.importProductModal.forceRerender(
                        this.communicationId,
                        meta.length ? meta : fallbackMeta,
                        false,
                        { tierImportMode: true }
                    )
                })
            })
        },

        onImportProductsCompleted() {
            this.$emit('import-completed')
        },

        extractDateComponents(dateValue) {
            if (!dateValue) return null
            const value = dateValue?.$date || dateValue
            if (!value) return null
            if (typeof value === 'string') {
                const match = value.match(/(\d{4})-(\d{2})-(\d{2})/)
                if (match) {
                    return {
                        year: parseInt(match[1], 10),
                        month: parseInt(match[2], 10),
                        day: parseInt(match[3], 10)
                    }
                }
            }
            if (value instanceof Date && !isNaN(value.getTime())) {
                return {
                    year: value.getFullYear(),
                    month: value.getMonth() + 1,
                    day: value.getDate()
                }
            }
            return null
        },

        formatDateForAPI(date) {
            const components = this.extractDateComponents(date)
            if (!components) return null
            const { year, month, day } = components
            const monthStr = String(month).padStart(2, '0')
            const dayStr = String(day).padStart(2, '0')
            return `${year}-${monthStr}-${dayStr}T00:00:00.000Z`
        },

        formatDateForDisplay(date) {
            const components = this.extractDateComponents(date)
            if (!components) return '-'
            const { year, month, day } = components
            const monthStr = String(month).padStart(2, '0')
            const dayStr = String(day).padStart(2, '0')
            return `${monthStr}/${dayStr}/${year}`
        },

        convertDateForPicker(dateValue) {
            const components = this.extractDateComponents(dateValue)
            if (!components) return null
            const { year, month, day } = components
            return new Date(year, month - 1, day, 12, 0, 0)
        },

        displayFieldValue(def, values) {
            const raw = values && values[def.field_key]
            if (raw == null || raw === '') return '-'
            if (this.isDateField(def)) return this.formatDateForDisplay(raw)
            return raw
        },

        previewTags(values, tierLevel, limit = 2) {
            const fields = this.fieldsByTier[tierLevel] || []
            const tags = []
            for (const def of fields) {
                const val = values && values[def.field_key]
                if (val == null || val === '') continue
                const display = this.isDateField(def) ? this.formatDateForDisplay(val) : val
                if (display == null || display === '') continue
                if (display.length > 10) {
                    tags.push({ key: def.field_key, label: def.field_name, value: display.slice(0, 8) + '...' })
                } else {
                    tags.push({ key: def.field_key, label: def.field_name, value: display })
                }
                if (tags.length >= limit) break
            }
            return tags
        },

        showToast(message, options = {}) {
            const toastKey = `${message}_${options.variant || 'default'}`
            if (this.activeToasts.has(toastKey)) {
                return
            }
            this.activeToasts.add(toastKey)
            this.$bvToast.toast(message, {
                title: options.title || 'Notification',
                variant: options.variant || 'info',
                solid: true,
                toaster: 'b-toaster-top-center',
                autoHideDelay: options.autoHideDelay || 3000,
                ...options
            })
            setTimeout(() => {
                this.activeToasts.delete(toastKey)
            }, (options.autoHideDelay || 3000) + 500)
        },

        toggleExpand(key) {
            this.$set(this.expandedProducts, key, !this.expandedProducts[key])
        },

        getNode(level, indices) {
            const { productIndex, tier1Index, tier2Index, tier3Index, tier4Index } = indices
            const product = this.products[productIndex]
            if (!product) return null
            if (level === 0) return product
            const t1 = product.tier_1_data?.[tier1Index]
            if (level === 1) return t1
            const t2 = t1?.tier_2_data?.[tier2Index]
            if (level === 2) return t2
            const t3 = t2?.tier_3_data?.[tier3Index]
            if (level === 3) return t3
            return t3?.tier_4_data?.[tier4Index]
        },

        startEditTier(level, indices) {
            if (this.disabled || (level === 4 && this.showQuantityUnit)) return
            this.ensureDefinitionsLoaded().then(() => {
                this.editingTier = { level, ...indices }
                this.tempTierValues = this.extractTierData(level, indices)
            })
        },

        extractTierData(level, indices) {
            if (level === 0) {
                const product = this.products[indices.productIndex]
                return {
                    name: product?.name || '',
                    quantity: product?.quantity ?? '',
                    unitOfMeasure: product?.unitOfMeasure || ''
                }
            }
            const node = this.getNode(level, indices) || {}
            const values = node.values || {}
            const data = {}
            ;(this.fieldsByTier[level] || []).forEach(def => {
                const raw = values[def.field_key]
                data[def.field_key] = this.isDateField(def)
                    ? this.convertDateForPicker(raw)
                    : (raw || '')
            })
            return data
        },

        saveTier() {
            if (!this.editingTier) return

            const { level, productIndex, tier1Index, tier2Index, tier3Index, tier4Index } = this.editingTier
            const product = this.products[productIndex]

            if (level === 0 && (!this.tempTierValues.name || !this.tempTierValues.name.trim())) {
                this.showToast('Product description is required', { title: 'Validation Error', variant: 'danger' })
                return
            }

            let updatedProduct = JSON.parse(JSON.stringify(product))

            if (level === 0) {
                updatedProduct.name = this.tempTierValues.name
                updatedProduct.quantity = this.tempTierValues.quantity ?? ''
                updatedProduct.unitOfMeasure = this.tempTierValues.unitOfMeasure || ''
            } else {
                const values = {}
                ;(this.fieldsByTier[level] || []).forEach(def => {
                    let val = this.tempTierValues[def.field_key]
                    if (val == null || val === '') return
                    if (this.isDateField(def)) {
                        val = this.formatDateForAPI(val)
                        if (!val) return
                    }
                    values[def.field_key] = typeof val === 'string' ? val.trim() : String(val)
                })

                if (level === 1) {
                    updatedProduct.tier_1_data[tier1Index] = {
                        ...updatedProduct.tier_1_data[tier1Index],
                        values
                    }
                } else if (level === 2) {
                    updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index] = {
                        ...updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index],
                        values
                    }
                } else if (level === 3) {
                    updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index] = {
                        ...updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index],
                        values
                    }
                } else if (level === 4) {
                    updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data[tier4Index] = {
                        ...updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data[tier4Index],
                        values
                    }
                }
            }

            this.$set(this.products, productIndex, updatedProduct)
            this.emitProducts({ type: 'update', index: productIndex, product: updatedProduct })
            this.showToast('Tier updated successfully', { title: 'Success', variant: 'success' })
            this.cancelEditTier()
        },

        cancelEditTier() {
            this.editingTier = null
            this.tempTierValues = {}
        },

        deleteTierEntry(level, indices) {
            if (this.disabled || (level === 4 && this.showQuantityUnit)) return
            const { productIndex, tier1Index, tier2Index, tier3Index, tier4Index } = indices

            this.$bvModal.msgBoxConfirm(
                'Are you sure you want to delete this entry? This action cannot be undone.',
                {
                    title: 'Confirm Delete',
                    size: 'md',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Delete',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                }
            )
                .then(value => {
                    if (value) {
                        if (level === 0) {
                            const deleted = this.products[productIndex]
                            this.products.splice(productIndex, 1)
                            this.emitProducts({ type: 'delete', index: productIndex, product: deleted })
                            this.showToast('Product deleted successfully', { title: 'Success', variant: 'success' })
                        } else {
                            const product = this.products[productIndex]
                            let updatedProduct = JSON.parse(JSON.stringify(product))

                            if (level === 1) {
                                updatedProduct.tier_1_data.splice(tier1Index, 1)
                            } else if (level === 2) {
                                updatedProduct.tier_1_data[tier1Index].tier_2_data.splice(tier2Index, 1)
                            } else if (level === 3) {
                                updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data.splice(tier3Index, 1)
                            } else if (level === 4) {
                                updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data.splice(tier4Index, 1)
                            }

                            this.$set(this.products, productIndex, updatedProduct)
                            this.emitProducts({ type: 'update', index: productIndex, product: updatedProduct })
                            this.showToast('Entry deleted successfully', { title: 'Success', variant: 'success' })
                        }
                    }
                })
                .catch(() => {})
        },

        createEmptyTierRecord(level) {
            const record = {
                id: (typeof crypto !== 'undefined' && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `tier-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                values: {}
            }
            if (level === 1) record.tier_2_data = []
            if (level === 2) record.tier_3_data = []
            if (level === 3) record.tier_4_data = []
            return record
        },

        addTierEntry(level, indices) {
            if (this.disabled || (level === 4 && this.showQuantityUnit)) return

            if (level === 0) {
                this.isAddingNewProduct = true
                this.tempTierValues = { name: '', quantity: '', unitOfMeasure: '' }
                this.editingTier = null
                return
            }

            const { productIndex, tier1Index, tier2Index, tier3Index } = indices || {}
            if (productIndex === undefined || productIndex === null) return

            const product = this.products[productIndex]
            if (!product) return

            let updatedProduct = JSON.parse(JSON.stringify(product))
            if (!updatedProduct.tier_1_data) {
                updatedProduct.tier_1_data = []
            }

            let newIndices = { productIndex }
            let expandKey = `p-${productIndex}`

            switch (level) {
            case 1: {
                updatedProduct.tier_1_data.push(this.createEmptyTierRecord(1))
                const tier1IndexNew = updatedProduct.tier_1_data.length - 1
                newIndices = { productIndex, tier1Index: tier1IndexNew }
                expandKey = `p-${productIndex}`
                break
            }
            case 2: {
                if (!updatedProduct.tier_1_data[tier1Index]) return
                if (!updatedProduct.tier_1_data[tier1Index].tier_2_data) {
                    updatedProduct.tier_1_data[tier1Index].tier_2_data = []
                }
                updatedProduct.tier_1_data[tier1Index].tier_2_data.push(this.createEmptyTierRecord(2))
                const tier2IndexNew = updatedProduct.tier_1_data[tier1Index].tier_2_data.length - 1
                newIndices = { productIndex, tier1Index, tier2Index: tier2IndexNew }
                expandKey = `t1-${productIndex}-${tier1Index}`
                break
            }
            case 3: {
                if (!updatedProduct.tier_1_data[tier1Index]?.tier_2_data?.[tier2Index]) return
                if (!updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data) {
                    updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data = []
                }
                updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data.push(this.createEmptyTierRecord(3))
                const tier3IndexNew = updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data.length - 1
                newIndices = { productIndex, tier1Index, tier2Index, tier3Index: tier3IndexNew }
                expandKey = `t2-${productIndex}-${tier1Index}-${tier2Index}`
                break
            }
            case 4: {
                if (!updatedProduct.tier_1_data[tier1Index]?.tier_2_data?.[tier2Index]?.tier_3_data?.[tier3Index]) return
                if (!updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data) {
                    updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data = []
                }
                updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data.push(this.createEmptyTierRecord(4))
                const tier4IndexNew = updatedProduct.tier_1_data[tier1Index].tier_2_data[tier2Index].tier_3_data[tier3Index].tier_4_data.length - 1
                newIndices = { productIndex, tier1Index, tier2Index, tier3Index, tier4Index: tier4IndexNew }
                expandKey = `t3-${productIndex}-${tier1Index}-${tier2Index}-${tier3Index}`
                break
            }
            default:
                return
            }

            this.$set(this.products, productIndex, updatedProduct)
            this.emitProducts({ type: 'update', index: productIndex, product: updatedProduct })
            this.$set(this.expandedProducts, expandKey, true)

            this.$nextTick(() => {
                this.startEditTier(level, newIndices)
            })

            this.showToast('Record added. Fill in the details and save.', {
                title: 'Success',
                variant: 'success',
                autoHideDelay: 2500
            })
        },

        cancelAddTier() {
            this.isAddingNewProduct = false
            this.tempTierValues = {}
            this.editingTier = null
        },

        addNewProduct() {
            if (this.editingTier !== null) {
                this.editingTier = null
            }
            this.isAddingNewProduct = true
            this.tempTierValues = { name: '', quantity: '', unitOfMeasure: '' }
        },

        cancelNewProduct() {
            this.isAddingNewProduct = false
            this.tempTierValues = {}
        },

        saveNewProduct() {
            if (!this.tempTierValues.name || this.tempTierValues.name.trim() === '') {
                this.showToast('Product description is required', {
                    title: 'Validation Error',
                    variant: 'danger',
                    autoHideDelay: 3000
                })
                return
            }

            const newProduct = {
                id: (typeof crypto !== 'undefined' && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `product-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                name: this.tempTierValues.name,
                quantity: this.tempTierValues.quantity ?? '',
                unitOfMeasure: this.tempTierValues.unitOfMeasure || '',
                tier_1_data: []
            }

            this.products.push(newProduct)
            this.emitProducts({ type: 'add', index: this.products.length - 1, product: newProduct })
            this.isAddingNewProduct = false
            this.showToast('Product added successfully', {
                title: 'Success',
                variant: 'success',
                autoHideDelay: 3000
            })
            this.tempTierValues = {}
        },

        resetEditingStates() {
            this.editingTier = null
            this.tempTierValues = {}
            this.expandedProducts = {}
            this.isAddingNewProduct = false
        },

        getProducts() {
            return JSON.parse(JSON.stringify(this.products))
        },

        validate() {
            if (!this.products || this.products.length === 0) {
                this.showToast('There must be at least one product defined', {
                    title: 'Validation Error',
                    variant: 'danger'
                })
                return false
            }
            for (let i = 0; i < this.products.length; i++) {
                if (!this.products[i].name || !String(this.products[i].name).trim()) {
                    this.showToast(`Product #${i + 1} name is required`, {
                        title: 'Validation Error',
                        variant: 'danger'
                    })
                    return false
                }
            }
            return true
        }
    }
}
